// xlsxParser — Real Excel (.xlsx / .xls) reader using SheetJS.
// Returns the same shape as parseCsv() plus sheetNames + rowSheets so the
// "Source: Uploaded Master · file · Sheet" badge can be stamped per row.

import * as XLSX from 'xlsx';

/**
 * Parse an .xlsx / .xls file. Multi-sheet workbooks are flattened into a
 * single (headers, rows) pair using the FIRST sheet's header row as the
 * canonical column layout. Subsequent sheets contribute their data rows
 * (header row skipped) preserving column order. Each emitted row is tagged
 * with the sheet it came from in `rowSheets[i]`.
 *
 * Empty rows (every cell blank) are dropped.
 *
 * @param {File} file
 * @returns {Promise<{ headers: string[], rows: string[][], sheetNames: string[], rowSheets: string[] }>}
 */
export async function parseXlsx(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetNames = wb.SheetNames || [];

  let headers = [];
  const rows = [];
  const rowSheets = [];

  sheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    if (!ws) return;
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
    if (!aoa.length) return;

    // First non-empty sheet drives the canonical headers
    const sheetHeaders = (aoa[0] || []).map((h) => (h == null ? '' : String(h).trim()));
    if (!headers.length) headers = sheetHeaders;

    // Append data rows (skip the header row of every sheet). Pad/truncate to canonical width.
    aoa.slice(1).forEach((r) => {
      const cells = headers.map((_, i) => (r[i] == null ? '' : String(r[i]).trim()));
      if (cells.some((c) => c !== '')) {
        rows.push(cells);
        rowSheets.push(name);
      }
    });
  });

  return { headers, rows, sheetNames, rowSheets };
}
